import { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import { Edit3, KeyRound, Plus, ShieldCheck, Stethoscope, UserCheck, UserX, Users, Briefcase } from 'lucide-react-native';
import type { LucideIcon } from 'lucide-react-native';

import { Badge, Button, Card, EmptyState, SkeletonRows, Sheet, StatTile, TextField, Select, type SelectOption } from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { usuariosQuery, crearUsuario, actualizarUsuario, restablecerPassword } from '@/services/firebase/usuarios';
import { useAuth } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { normalizar } from '@/utils/format';
import type { Usuario, Rol } from '@/types/domain';

/**
 * Arma el usuario nombre.apellido (minúscula, sin tildes). Toma solo la PRIMERA
 * palabra del nombre y la PRIMERA del apellido, aunque sean compuestos.
 */
function usuarioDesde(nombre: string, apellido: string): string {
  const primera = (s: string) => (normalizar(s).trim().split(/\s+/)[0] ?? '').replace(/[^a-z0-9]+/g, '');
  const n = primera(nombre);
  const a = primera(apellido);
  if (n && a) return `${n}.${a}`;
  return n || a;
}

const ROLES: { id: Rol; label: string; descripcion: string }[] = [
  { id: 'admin', label: 'Administradora', descripcion: 'Acceso total al sistema' },
  { id: 'doctor', label: 'Doctor/a', descripcion: 'Inventario lectura, registrar uso, solicitar material, subir archivos' },
  { id: 'recepcion', label: 'Recepción', descripcion: 'Ventas + libros Danna Zapata y Bruno' },
];

const ROL_TONO: Record<Rol, 'magenta' | 'info' | 'neutral'> = {
  admin: 'magenta',
  doctor: 'info',
  recepcion: 'neutral',
};

const AREA_META: Record<Rol, { label: string; descripcion: string; icon: LucideIcon; bg: string; fg: string }> = {
  admin: {
    label: 'Administración',
    descripcion: 'Acceso total al sistema',
    icon: ShieldCheck,
    bg: colors.magenta[100],
    fg: colors.magenta[700],
  },
  doctor: {
    label: 'Doctores y doctoras',
    descripcion: 'Atención clínica',
    icon: Stethoscope,
    bg: '#E0F2FE',
    fg: '#0369A1',
  },
  recepcion: {
    label: 'Recepción',
    descripcion: 'Ventas y caja',
    icon: Briefcase,
    bg: colors.crema[100],
    fg: colors.ink[700],
  },
};

export function UsuariosScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const { data, loading } = useFirestoreCollection(usuariosQuery);
  const yo = useAuth((s) => s.perfil);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Usuario | null>(null);

  const stats = useMemo(() => {
    const total = data.length;
    const activos = data.filter((u) => u.activo).length;
    const admins = data.filter((u) => u.rol === 'admin' && u.activo).length;
    return { total, activos, admins };
  }, [data]);

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 20, paddingBottom: 80 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        <StatTile label="Equipo total" value={String(stats.total)} accent="magenta" icon={<Users size={20} color={colors.magenta[700]} />} />
        <StatTile label="Activos" value={String(stats.activos)} accent="success" icon={<UserCheck size={20} color={colors.success[600]} />} />
        <StatTile label="Administradoras" value={String(stats.admins)} accent="warning" icon={<ShieldCheck size={20} color={colors.warning[600]} />} hint="Con poder total" />
      </View>

      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <View>
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 20, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
              Equipo
            </Text>
            <Text style={{ color: colors.ink[500], fontSize: 13, marginTop: 2 }}>
              Solo administradoras pueden crear y modificar.
            </Text>
          </View>
          <Button
            label="Invitar persona"
            iconLeft={<Plus size={16} color={colors.white} />}
            onPress={() => {
              setEditing(null);
              setOpen(true);
            }}
          />
        </View>
      </Card>

      {loading ? (
        <SkeletonRows rows={3} />
      ) : data.length === 0 ? (
        <Card>
          <EmptyState
            illustration="cat"
            title="Aún no hay equipo"
            message="Invitá a doctores y recepción para que cada quien tenga su acceso."
            actionLabel="Invitar primera persona"
            onAction={() => setOpen(true)}
          />
        </Card>
      ) : (
        <View style={{ flexDirection: wide ? 'row' : 'column', gap: 16, alignItems: 'stretch' }}>
          {(['admin', 'doctor', 'recepcion'] as Rol[]).map((rol) => {
            const usuariosRol = data.filter((u) => u.rol === rol);
            const meta = AREA_META[rol];
            const Icon = meta.icon;
            return (
              <View
                key={rol}
                style={{
                  flex: 1,
                  gap: 10,
                  backgroundColor: colors.white,
                  borderRadius: radius['2xl'],
                  borderWidth: 1,
                  borderColor: colors.ink[100],
                  padding: 14,
                }}
              >
                {/* Encabezado de columna */}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <View
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 11,
                      backgroundColor: meta.bg,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon size={18} color={meta.fg} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text
                      style={{
                        fontFamily: typography.display.fontFamily,
                        fontSize: 15,
                        fontWeight: typography.weights.bold,
                        color: colors.ink[900],
                      }}
                    >
                      {meta.label}
                    </Text>
                    <Text style={{ color: colors.ink[500], fontSize: 11 }}>
                      {usuariosRol.length} {usuariosRol.length === 1 ? 'persona' : 'personas'} · {meta.descripcion}
                    </Text>
                  </View>
                </View>
                {/* Personas de esta área */}
                <View style={{ gap: 8 }}>
                  {usuariosRol.length === 0 ? (
                    <View style={{ paddingVertical: 18, borderRadius: 14, backgroundColor: colors.crema[50], alignItems: 'center' }}>
                      <Text style={{ color: colors.ink[400], fontSize: 12 }}>Nadie todavía</Text>
                    </View>
                  ) : (
                    usuariosRol.map((u) => (
                      <UsuarioRow
                        key={u.uid}
                        usuario={u}
                        esYo={u.uid === yo?.uid}
                        onEdit={() => {
                          setEditing(u);
                          setOpen(true);
                        }}
                        onToggleActivo={() => actualizarUsuario(u.uid, { activo: !u.activo })}
                      />
                    ))
                  )}
                </View>
              </View>
            );
          })}
        </View>
      )}

      <UsuarioSheet visible={open} onClose={() => setOpen(false)} usuario={editing} />
    </ScrollView>
  );
}

function UsuarioRow({
  usuario,
  esYo,
  onEdit,
  onToggleActivo,
}: {
  usuario: Usuario;
  esYo: boolean;
  onEdit: () => void;
  onToggleActivo: () => void;
}) {
  const inactivo = !usuario.activo;
  return (
    <View
      style={{
        gap: 10,
        padding: 12,
        backgroundColor: colors.crema[50],
        borderRadius: 14,
        borderWidth: 1,
        borderColor: colors.ink[100],
        opacity: inactivo ? 0.7 : 1,
      }}
    >
      {/* Avatar + nombre + editar */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <View
          style={{
            width: 38,
            height: 38,
            borderRadius: 19,
            backgroundColor: colors.magenta[100],
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text style={{ color: colors.magenta[700], fontWeight: typography.weights.extrabold, fontSize: 15 }}>
            {usuario.nombre?.[0]?.toUpperCase() ?? '·'}
          </Text>
        </View>
        <View style={{ flex: 1, gap: 2 }}>
          <Text style={{ fontWeight: typography.weights.bold, color: colors.ink[900], fontSize: 14 }} numberOfLines={1}>
            {usuario.nombre} {esYo ? '(vos)' : ''}
          </Text>
          <Text style={{ fontSize: 11, color: colors.ink[500] }} numberOfLines={1}>
            {usuario.email}
          </Text>
        </View>
        <Pressable
          onPress={onEdit}
          style={({ hovered }) => ({
            width: 30,
            height: 30,
            borderRadius: 10,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: hovered ? colors.ink[100] : 'transparent',
            // @ts-ignore web only
            cursor: 'pointer',
          })}
        >
          <Edit3 size={15} color={colors.ink[600]} />
        </Pressable>
      </View>
      {/* Badges + desactivar */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <Badge label={usuario.rol} tone={ROL_TONO[usuario.rol]} />
        {inactivo ? <Badge label="Inactivo" tone="danger" /> : null}
        <View style={{ flex: 1 }} />
        {!esYo ? (
          <Pressable
            onPress={onToggleActivo}
            style={({ hovered }) => ({
              paddingHorizontal: 10,
              height: 30,
              borderRadius: 10,
              backgroundColor: inactivo ? (hovered ? colors.success[50] : 'transparent') : hovered ? colors.danger[50] : 'transparent',
              borderWidth: 1,
              borderColor: inactivo ? colors.success[500] : colors.danger[500],
              flexDirection: 'row',
              alignItems: 'center',
              gap: 5,
              // @ts-ignore web only
              cursor: 'pointer',
            })}
          >
            {inactivo ? <UserCheck size={13} color={colors.success[600]} /> : <UserX size={13} color={colors.danger[600]} />}
            <Text
              style={{
                fontSize: 11,
                fontWeight: typography.weights.bold,
                color: inactivo ? colors.success[600] : colors.danger[600],
              }}
            >
              {inactivo ? 'Reactivar' : 'Desactivar'}
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

function UsuarioSheet({ visible, onClose, usuario }: { visible: boolean; onClose: () => void; usuario: Usuario | null }) {
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [rol, setRol] = useState<Rol>('doctor');
  const [saving, setSaving] = useState(false);
  const [reseteando, setReseteando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    const partes = (usuario?.nombre ?? '').trim().split(/\s+/).filter(Boolean);
    setNombre(partes[0] ?? '');
    setApellido(partes.slice(1).join(' '));
    setRol(usuario?.rol ?? 'doctor');
    setError(null);
    setAviso(null);
    setSaving(false);
    setReseteando(false);
  }, [visible, usuario]);

  // En crear, el usuario se arma solo. En editar, no se cambia (es su login).
  const usuarioPreview = usuario ? usuario.email : usuarioDesde(nombre, apellido);
  const rolOptions: SelectOption[] = ROLES.map((r) => ({ value: r.id, label: r.label, hint: r.descripcion }));

  const onSave = async () => {
    setError(null);
    if (!nombre.trim() || !apellido.trim()) return setError('Poné nombre y apellido.');
    setSaving(true);
    try {
      const display = `${nombre.trim()} ${apellido.trim()}`.replace(/\s+/g, ' ').trim();
      if (usuario) {
        await actualizarUsuario(usuario.uid, { nombre: display, rol });
      } else {
        const username = usuarioDesde(nombre, apellido);
        await crearUsuario({ email: username, uid: username, nombre: display, rol });
      }
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  const onResetPassword = async () => {
    if (!usuario) return;
    const ok =
      typeof window === 'undefined'
        ? true
        : window.confirm(`¿Restablecer la contraseña de ${usuario.nombre}? La próxima vez que entre, la que escriba quedará como su nueva contraseña.`);
    if (!ok) return;
    setReseteando(true);
    setError(null);
    setAviso(null);
    try {
      await restablecerPassword(usuario.uid);
      setAviso('Listo. La próxima vez que entre, la contraseña que escriba queda como la nueva.');
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo restablecer.');
    } finally {
      setReseteando(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="md"
      title={usuario ? 'Editar usuario' : 'Nueva persona'}
      subtitle={usuario ? 'Actualizá nombre, apellido o rol.' : 'Poné nombre, apellido y rol. El usuario se arma solo.'}
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} />
          <Button label={usuario ? 'Guardar cambios' : 'Crear'} onPress={onSave} loading={saving} />
        </>
      }
    >
      <View style={{ flexDirection: 'row', gap: 12 }}>
        <View style={{ flex: 1 }}>
          <TextField label="Nombre" value={nombre} onChangeText={setNombre} placeholder="Jazmín" autoCapitalize="words" />
        </View>
        <View style={{ flex: 1 }}>
          <TextField label="Apellido" value={apellido} onChangeText={setApellido} placeholder="Veizaga" autoCapitalize="words" />
        </View>
      </View>

      {/* Usuario (se arma solo) */}
      <View style={{ gap: 6 }}>
        <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[700] }}>
          Usuario {usuario ? '' : '(se arma solo)'}
        </Text>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            paddingHorizontal: 14,
            height: 48,
            borderRadius: 12,
            backgroundColor: colors.crema[50],
            borderWidth: 1.5,
            borderColor: colors.ink[100],
          }}
        >
          <Users size={16} color={colors.ink[400]} />
          <Text style={{ flex: 1, fontSize: 15, fontWeight: typography.weights.bold, color: usuarioPreview ? colors.magenta[700] : colors.ink[400] }}>
            {usuarioPreview || 'nombre.apellido'}
          </Text>
        </View>
        <Text style={{ fontSize: 11.5, color: colors.ink[400] }}>
          {usuario
            ? 'Es su login y no cambia. La contraseña la pone cada una al entrar.'
            : 'Con este usuario entra. La contraseña la crea ella en su primer ingreso.'}
        </Text>
      </View>

      <Select label="Rol" value={rol} onChange={(v) => v && setRol(v as Rol)} options={rolOptions} />

      {/* Restablecer contraseña — solo al editar */}
      {usuario ? (
        <View style={{ borderTopWidth: 1, borderTopColor: colors.ink[100], paddingTop: 14, gap: 8 }}>
          <Pressable
            onPress={onResetPassword}
            disabled={reseteando}
            style={({ hovered }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              height: 44,
              borderRadius: 12,
              borderWidth: 1.5,
              borderColor: colors.warning[500],
              backgroundColor: hovered ? colors.warning[50] : colors.white,
              opacity: reseteando ? 0.6 : 1,
              // @ts-ignore web only
              cursor: reseteando ? 'default' : 'pointer',
            })}
          >
            <KeyRound size={16} color={colors.warning[600]} />
            <Text style={{ fontSize: 14, fontWeight: typography.weights.bold, color: colors.warning[600] }}>
              {reseteando ? 'Restableciendo…' : 'Restablecer contraseña'}
            </Text>
          </Pressable>
          {aviso ? (
            <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.success[50] }}>
              <Text style={{ color: colors.success[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{aviso}</Text>
            </View>
          ) : null}
        </View>
      ) : null}

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}
